/**
 * Imprimer ou exporter une gamme d'entretien (registre B22, première brique).
 *
 * La page EST le document : une feuille A4 que l'impression du navigateur — ou « Enregistrer en PDF » —
 * reproduit telle quelle, comme les planches d'étiquettes QR. Aucune bibliothèque PDF n'est ajoutée.
 *
 * Tout ce qui s'affiche vient de `construireDocumentGamme` : la variante choisie (sans prix, avec prix,
 * interne) décide de ce qui EXISTE dans le document. Rien n'est « caché » : un champ que la variante
 * n'autorise pas n'est ni dans la page, ni dans le classeur Excel exporté.
 *
 * Le titre de la page est le nom du document (« KAESER ESD442SFC – ESD.3 (sans prix) ») : c'est le nom
 * que le navigateur propose pour le PDF.
 *
 * On y arrive depuis la page « Gammes » (version affichée) ou depuis la fiche d'une machine (version en
 * vigueur, avec la machine en en-tête) : `?gamme=…&version=…&machine=…`, et `&variante=INTERNE` pour
 * choisir la variante d'emblée.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ApiError, api, type Asset, type GammeDetail, type GammeVersion } from '../api/client';
import { useDroitsGammes } from '../auth/useDroits';
import { prix, quantite } from '../domaine/gammes';
import { chargerExcel, construireClasseur, octetsClasseur } from '../domaine/gammes-excel';
import {
  type DocumentGamme,
  type MachineDocument,
  type PalierDocument,
  type VarianteGamme,
  AVERTISSEMENT_INTERNE,
  TEXTE_VARIANTE,
  VARIANTES_GAMME,
  construireDocumentGamme,
  varianteDisponible,
  nomDocument,
  nomFichier,
  phraseEdition,
  phraseVersion,
  remarquesDuPalier,
  colonnesFeuille,
  type CleColonne,
} from '../domaine/gammes-document';
import { aDesPrixDeVente, niveauxAvecPrixCalcules } from '../domaine/gammes-prix-calcules';
import {
  montantMarge,
  montantSousTotal,
  recapitulatifAchat,
  recapitulatifMarges,
  recapitulatifSousTotaux,
  reserveAdaptable,
  reserveMarge,
  reserveSousTotal,
} from '../domaine/gammes-sous-totaux';
import { aujourdhuiLocal, estEnVigueur } from '../domaine/gammes-versions';
import { useIdentiteEspace } from '../domaine/identite-espace';
import { CONSEIL_EN_TETES_NAVIGATEUR, installerPageGamme } from '../domaine/page-impression';

const TYPE_XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function telecharger(nom: string, octets: Uint8Array, type: string): void {
  const blob = new Blob([octets as BlobPart], { type });
  const url = URL.createObjectURL(blob);
  const lien = window.document.createElement('a');
  lien.href = url;
  lien.download = nom;
  window.document.body.appendChild(lien);
  lien.click();
  lien.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function machineDocument(a: Asset): MachineDocument {
  return {
    designation: a.label ?? a.model ?? 'Machine',
    modele: a.model,
    referenceConstructeur: a.manufacturerReference,
    numeroSerie: a.serialNumber,
    site: a.siteName,
    client: a.customerName,
  };
}

export function GammeImpressionPage() {
  const [params] = useSearchParams();
  const gammeId = params.get('gamme');
  const versionVoulue = params.get('version');
  const machineId = params.get('machine');

  const droits = useDroitsGammes();
  const identite = useIdentiteEspace();

  const [detail, setDetail] = useState<GammeDetail | null>(null);
  const [machine, setMachine] = useState<MachineDocument | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  // La variante peut venir de l'adresse (« ?variante=INTERNE ») : un lien se partage et se rejoue tel quel.
  const [variante, setVariante] = useState<VarianteGamme>(() => {
    const voulue = params.get('variante');
    return VARIANTES_GAMME.find((v) => v === voulue) ?? 'SANS_PRIX';
  });
  const [choisis, setChoisis] = useState<ReadonlySet<string>>(new Set());
  const [unParPage, setUnParPage] = useState(() => params.get('unparpage') !== '0');
  const [exportEnCours, setExportEnCours] = useState(false);
  const [erreurExport, setErreurExport] = useState<string | null>(null);

  useEffect(() => {
    if (!gammeId) {
      setErreur('Aucune gamme à imprimer : ouvrez cette page depuis une gamme.');
      return;
    }
    let actif = true;
    setDetail(null);
    setErreur(null);
    api
      .maintenanceProfile(gammeId)
      .then((d) => {
        if (actif) setDetail(d);
      })
      .catch((e: unknown) => {
        if (actif) setErreur(e instanceof ApiError ? e.message : 'Gamme illisible.');
      });
    return () => {
      actif = false;
    };
  }, [gammeId]);

  useEffect(() => {
    setMachine(null);
    if (!machineId) return;
    let actif = true;
    api
      .asset(machineId)
      .then((a) => {
        if (actif) setMachine(machineDocument(a));
      })
      .catch(() => {
        // La machine ne bloque pas l'impression de la gamme : le document sort simplement sans ce bloc.
      });
    return () => {
      actif = false;
    };
  }, [machineId]);

  const version: GammeVersion | null = useMemo(() => {
    if (!detail) return null;
    return (
      detail.versions.find((v) => v.id === versionVoulue) ??
      detail.versions.find((v) => estEnVigueur(detail, v)) ??
      detail.versions[0] ??
      null
    );
  }, [detail, versionVoulue]);

  // Tous les paliers sont cochés d'emblée, et à nouveau si l'on change de version.
  useEffect(() => {
    setChoisis(new Set((version?.contenu.levels ?? []).map((n) => n.levelKey)));
  }, [version?.id]);

  const prixDisponibles = useMemo(
    () => droits.peutVoirPrix && (version?.contenu.levels ?? []).some((n) => n.lines.some((l) => 'publicPrice' in l)),
    [droits.peutVoirPrix, version],
  );
  // La version porte-t-elle un prix de VENTE calculé à sa publication (lot P2) ? Sans lui, « avec prix » n'existe pas.
  const aPrixDeVente = useMemo(() => (version ? aDesPrixDeVente(niveauxAvecPrixCalcules(version)) : false), [version]);
  // Une variante avec prix devenue impossible (droit perdu, version sans prix) retombe sur « sans prix ».
  // « Avec prix » sans prix de vente (version publiée avant leur calcul : le prix du tarif n’est pas le prix de vente)
  // retombe aussi sur « sans prix », même demandée par l’adresse.
  const varianteEffective: VarianteGamme =
    (!prixDisponibles && variante !== 'SANS_PRIX') || varianteDisponible(variante, aPrixDeVente) !== null ? 'SANS_PRIX' : variante;

  const resultat = useMemo(() => {
    if (!detail || !version) return null;
    return construireDocumentGamme({
      gamme: detail,
      version,
      enVigueur: estEnVigueur(detail, version),
      variante: varianteEffective,
      paliersChoisis: choisis,
      peutVoirPrix: droits.peutVoirPrix,
      machine,
      entreprise: identite?.nom ?? null,
      edition: aujourdhuiLocal(),
    });
  }, [detail, version, varianteEffective, choisis, droits.peutVoirPrix, machine, identite]);

  const doc = resultat?.ok ? resultat.document : null;

  // Le titre de la page devient le nom du document : c'est le nom que Chrome et Edge proposent au PDF.
  const nom = doc ? nomDocument(doc) : null;
  useEffect(() => {
    if (!nom) return;
    const avant = window.document.title;
    window.document.title = nom;
    return () => {
      window.document.title = avant;
    };
  }, [nom]);

  // Marges de la feuille : une règle @page ordinaire, ajoutée tant que cette page est affichée (voir
  // domaine/page-impression.ts : les pages nommées perdaient leurs marges à partir de la 3e feuille dans Edge).
  const interne = doc?.interne ?? false;
  useEffect(() => installerPageGamme(window.document, interne), [interne]);

  async function exporter() {
    if (!doc) return;
    setExportEnCours(true);
    setErreurExport(null);
    try {
      const excel = await chargerExcel();
      const octets = await octetsClasseur(construireClasseur(excel, doc));
      telecharger(nomFichier(doc, 'xlsx'), octets, TYPE_XLSX);
    } catch (e) {
      setErreurExport(`Le classeur n’a pas pu être préparé${e instanceof Error ? ` : ${e.message}` : '.'}`);
    } finally {
      setExportEnCours(false);
    }
  }

  const retour = machineId ? (
    <Link to={`/parc?asset=${machineId}`}>← Fiche machine</Link>
  ) : (
    <Link to={gammeId ? `/gammes?gamme=${gammeId}${version ? `&version=${version.id}` : ''}` : '/gammes'}>← Gammes</Link>
  );

  function basculer(levelKey: string) {
    setChoisis((courant) => {
      const suivant = new Set(courant);
      if (suivant.has(levelKey)) suivant.delete(levelKey);
      else suivant.add(levelKey);
      return suivant;
    });
  }

  return (
    <>
      {/* Tout ce bloc disparaît à l'impression : seule la feuille sort. */}
      <div className="barre-impression">
        <div className="fil-ariane">{retour}</div>

        <div className="entete-page">
          <div>
            <h1>Imprimer la gamme</h1>
            <p>
              {detail && version
                ? `${detail.label} — ${phraseVersion({
                    numero: version.versionNumber,
                    statut: version.statut,
                    enVigueur: estEnVigueur(detail, version),
                    effet: version.validFrom,
                  })}`
                : erreur
                  ? 'Gamme indisponible.'
                  : 'Préparation du document…'}
            </p>
          </div>
          <div className="actions">
            <button className="secondaire" disabled={!doc || exportEnCours} onClick={() => void exporter()}>
              {exportEnCours ? 'Préparation du classeur…' : 'Exporter pour Excel'}
            </button>
            <button disabled={!doc} onClick={() => window.print()}>
              Imprimer / Enregistrer en PDF
            </button>
          </div>
        </div>

        {erreur && <div className="message erreur">{erreur}</div>}
        {erreurExport && <div className="message erreur">{erreurExport}</div>}
        <div className="message info">{CONSEIL_EN_TETES_NAVIGATEUR}</div>

        {version && (
          <div className="carte">
            <div className="carte-corps options-impression">
              <fieldset>
                <legend>Variante</legend>
                {VARIANTES_GAMME.map((v) => {
                  const texte = TEXTE_VARIANTE[v];
                  const suspendue = varianteDisponible(v, aPrixDeVente);
                  const indisponible = (texte.exigePrix && !prixDisponibles) || suspendue !== null;
                  return (
                    <label key={v} className={`option-impression${indisponible ? ' indisponible' : ''}`}>
                      <input
                        type="radio"
                        name="variante"
                        checked={varianteEffective === v}
                        disabled={indisponible}
                        onChange={() => setVariante(v)}
                      />{' '}
                      <strong>{texte.libelle}</strong>
                      <span className="aide">{suspendue ?? texte.description}</span>
                    </label>
                  );
                })}
                {!prixDisponibles && (
                  <p className="aide">
                    {droits.peutVoirPrix
                      ? 'Cette version ne porte aucun prix : seule la variante sans prix est possible.'
                      : 'Votre compte ne voit pas les prix : seule la variante sans prix est possible.'}
                  </p>
                )}
              </fieldset>

              <fieldset>
                <legend>Paliers à imprimer</legend>
                {version.contenu.levels.length === 0 && <p className="aide">Cette version ne contient aucun palier.</p>}
                {version.contenu.levels.map((n) => (
                  <label key={n.levelKey} className="option-impression">
                    <input type="checkbox" checked={choisis.has(n.levelKey)} onChange={() => basculer(n.levelKey)} />{' '}
                    <strong>Palier {n.code}</strong>
                    <span className="aide">{n.label}</span>
                  </label>
                ))}
                <div className="saisie-ligne">
                  <button
                    className="secondaire discret"
                    onClick={() => setChoisis(new Set(version.contenu.levels.map((n) => n.levelKey)))}
                  >
                    Tous
                  </button>
                  <button className="secondaire discret" onClick={() => setChoisis(new Set())}>
                    Aucun
                  </button>
                </div>
                <label className="option-impression">
                  <input type="checkbox" checked={unParPage} onChange={(e) => setUnParPage(e.target.checked)} />{' '}
                  <strong>Un palier par page</strong>
                  <span className="aide">Sinon, les paliers se suivent ; un palier qui tient sur une page n’est jamais coupé.</span>
                </label>
              </fieldset>
            </div>
          </div>
        )}

        {resultat && !resultat.ok && <div className="message avertissement">{resultat.raison}</div>}

        {machineId && !machine && detail && (
          <p className="aide">Les informations de la machine n’ont pas pu être lues : le document sort sans leur bloc.</p>
        )}
      </div>

      {doc && <FeuilleGamme doc={doc} unParPage={unParPage} />}
    </>
  );
}

function FeuilleGamme({ doc, unParPage }: { doc: DocumentGamme; unParPage: boolean }) {
  const classes = ['document-gamme', unParPage ? 'un-par-page' : '', doc.interne ? 'interne' : ''].filter(Boolean).join(' ');
  return (
    <article className={classes}>
      <header className="document-entete">
        {doc.entreprise && <div className="document-entreprise">{doc.entreprise}</div>}
        <h1>Gamme d’entretien</h1>
        <h2>{doc.titre}</h2>
        {doc.interne && <div className="document-alerte-interne">{AVERTISSEMENT_INTERNE}</div>}

        <dl className="document-meta">
          <dt>Marque</dt>
          <dd>{doc.marque}</dd>
          {doc.modeles.length > 0 && (
            <>
              <dt>Modèles</dt>
              <dd>{doc.modeles.join(' · ')}</dd>
            </>
          )}
          {doc.references.length > 0 && (
            <>
              <dt>Réf. constructeur</dt>
              <dd>{doc.references.join(' ou ')}</dd>
            </>
          )}
          <dt>Version</dt>
          <dd>
            {phraseVersion(doc.version)} — {phraseEdition(doc).toLowerCase()}
          </dd>
        </dl>

        {doc.machine && (
          <div className="document-machine">
            <strong>Machine :</strong> {doc.machine.designation}
            {doc.machine.modele && ` — ${doc.machine.modele}`}
            {doc.machine.referenceConstructeur && ` (réf. ${doc.machine.referenceConstructeur})`}
            {doc.machine.numeroSerie && ` — n° ${doc.machine.numeroSerie}`}
            {(doc.machine.site || doc.machine.client) && (
              <>
                <br />
                {[doc.machine.client, doc.machine.site].filter(Boolean).join(' — ')}
              </>
            )}
          </div>
        )}
      </header>

      {doc.recapitulatif && (
        <p className="document-recap">
          <strong>{titreRecapitulatif(doc)}</strong> {recapitulatifSousTotaux(doc.recapitulatif)}
          {doc.recapitulatifAdaptable && (
            <>
              <br />
              <strong>Coût d’achat estimé par palier :</strong> {recapitulatifAchat(doc.recapitulatifAdaptable)}
            </>
          )}
          {doc.recapitulatifCout && (
            <>
              <br />
              <strong>Coût d’achat réel HT par palier :</strong> {recapitulatifSousTotaux(doc.recapitulatifCout)}
            </>
          )}
          {doc.recapitulatifMarge && (
            <>
              <br />
              <strong>Marge par palier :</strong> {recapitulatifMarges(doc.recapitulatifMarge)}
            </>
          )}
        </p>
      )}

      {doc.paliers.map((p) => (
        <PalierFeuille key={p.code} palier={p} doc={doc} />
      ))}
    </article>
  );
}

/** Le titre du récapitulatif en tête de feuille : il dit de QUEL prix sont faits les sous-totaux. */
function titreRecapitulatif(doc: DocumentGamme): string {
  if (doc.source === 'VENTE') return doc.interne ? 'Sous-total au prix de vente HT par palier :' : 'Sous-total HT par palier :';
  return 'Sous-total au prix du tarif HT par palier :';
}

const montant = (centimes: number | null | undefined): string =>
  centimes === null || centimes === undefined ? '—' : prix(centimes / 100);

function PalierFeuille({ palier, doc }: { palier: PalierDocument; doc: DocumentGamme }) {
  const cols = colonnesFeuille(doc);
  const nbColonnes = cols.length;
  const iTotal = cols.findIndex((c) => c.cle === 'total');
  const reserve = palier.sousTotal ? reserveSousTotal(palier.sousTotal) : null;
  // Sans coût d'achat réel, la variante interne garde la lecture d'avant : un coût ESTIMÉ.
  const estime = doc.interne && !doc.avecAchat && palier.sousTotalAdaptable ? palier.sousTotalAdaptable : null;
  const reserveAchat = estime ? reserveAdaptable(estime) : null;
  const reserveDeMarge = palier.marge ? reserveMarge(palier.marge) : null;
  // Les remarques de ligne vont EN BAS du palier (un numéro de renvoi dans la désignation), jamais dans la colonne.
  const { renvois, remarques } = remarquesDuPalier(palier.lignes);

  const cellule = (cle: CleColonne, l: PalierDocument['lignes'][number], i: number) => {
    switch (cle) {
      case 'designation':
        return (
          <td>
            {l.designation}
            {l.genre && <em className="document-genre"> ({l.genre})</em>}
            {renvois[i] != null && <sup className="document-renvoi">{renvois[i]}</sup>}
          </td>
        );
      case 'reference':
        return <td className="col-reference">{l.reference ?? '—'}</td>;
      case 'quantite':
        return <td className="col-qte">{quantite(l.quantite, l.unite)}</td>;
      case 'tarif':
        return <td className="col-nombre">{prix(l.prixTarif)}</td>;
      case 'prix':
        return <td className="col-nombre">{prix(l.prixPublic)}</td>;
      case 'total':
        return <td className="col-nombre">{montant(l.totalCentimes)}</td>;
      case 'refAdaptable':
        return <td className="col-reference">{l.referenceAdaptable ?? '—'}</td>;
      case 'prixAdaptable':
        return <td className="col-nombre">{prix(l.prixAdaptable)}</td>;
      case 'totalAdaptable':
        return <td className="col-nombre">{montant(l.totalAdaptableCentimes)}</td>;
      case 'prixAchat':
        return <td className="col-nombre">{prix(l.prixAchat)}</td>;
      case 'totalAchat':
        return <td className="col-nombre">{montant(l.totalAchatCentimes)}</td>;
    }
  };

  // Le montant d'un sous-total tombe sous la colonne qu'il additionne ; les autres cellules de la ligne restent vides.
  const ligneDeTotal = (
    cle: string,
    libelle: string,
    montants: Partial<Record<CleColonne, { texte: string; incomplet?: boolean }>>,
    classe = 'document-sous-total',
  ) => (
    <tr key={cle} className={classe}>
      <td colSpan={iTotal}>{libelle}</td>
      {cols.slice(iTotal).map((c) => {
        const m = montants[c.cle];
        return (
          <td key={c.cle} className={`col-nombre${m?.incomplet ? ' incomplet' : ''}`}>
            {m?.texte ?? ''}
          </td>
        );
      })}
    </tr>
  );

  const libelleSousTotal = `Sous-total HT${
    doc.source === 'VENTE' ? (doc.interne ? ' au prix de vente' : '') : ' au prix du tarif'
  } — palier ${palier.code}`;

  return (
    <section className="document-palier">
      {doc.interne && <div className="document-alerte-interne petite">{AVERTISSEMENT_INTERNE}</div>}
      <h3>
        Palier {palier.code} — {palier.libelle}
      </h3>
      <p className="document-cadence">{palier.cadence}</p>

      {palier.lignes.length === 0 ? (
        <p className="aide">Aucune ligne pour ce palier.</p>
      ) : (
        <table>
          <thead>
            <tr>
              {cols.map((c) => (
                <th key={c.cle} className={c.classe || undefined}>
                  {c.titre}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {palier.lignes.map((l, i) => (
              <tr key={i}>
                {cols.map((c) => (
                  <Fragment key={c.cle}>{cellule(c.cle, l, i)}</Fragment>
                ))}
              </tr>
            ))}
          </tbody>
          {palier.sousTotal && iTotal >= 0 && (
            <tfoot>
              {ligneDeTotal('sous-total', libelleSousTotal, {
                total: { texte: montantSousTotal(palier.sousTotal), incomplet: !palier.sousTotal.complet },
                ...(doc.interne && doc.avecEquivalents && palier.sousTotalAdaptable
                  ? { totalAdaptable: { texte: prix(palier.sousTotalAdaptable.adaptableCentimes / 100) } }
                  : {}),
                ...(doc.avecAchat && palier.sousTotalAchat
                  ? { totalAchat: { texte: montantSousTotal(palier.sousTotalAchat), incomplet: !palier.sousTotalAchat.complet } }
                  : {}),
              })}
              {palier.marge &&
                ligneDeTotal(
                  'marge',
                  `Marge HT — palier ${palier.code}`,
                  { total: { texte: montantMarge(palier.marge), incomplet: !palier.marge.complete } },
                  'document-sous-total document-achat',
                )}
              {estime &&
                ligneDeTotal(
                  'estime',
                  `Coût d’achat estimé HT — palier ${palier.code}`,
                  { total: { texte: prix(estime.achatCentimes / 100), incomplet: !estime.achatComplet } },
                  'document-sous-total document-achat',
                )}
              {reserve && (
                <tr className="document-reserve">
                  <td colSpan={nbColonnes}>{reserve}</td>
                </tr>
              )}
              {reserveDeMarge && (
                <tr className="document-reserve">
                  <td colSpan={nbColonnes}>{reserveDeMarge}</td>
                </tr>
              )}
              {reserveAchat && (
                <tr className="document-reserve">
                  <td colSpan={nbColonnes}>{reserveAchat}</td>
                </tr>
              )}
            </tfoot>
          )}
        </table>
      )}
      {remarques.length > 0 && (
        <ol className="document-remarques">
          {remarques.map((texte, i) => (
            <li key={i} value={i + 1}>
              {texte}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
